import type { Recipient } from '@/types/messaging';

interface PersonalizeOptions {
  body: string;
  recipient: Recipient;
  signature?: string;
  hasPlaceholders: boolean;
  hasProperties: boolean;
  isTemplate: boolean;
  currentUser?: { displayName?: string; email?: string; phoneNumber?: string };
}

/**
 * Resolve `{{property}}` entity placeholders inside template variable values.
 *
 * When users fill template variables using the "#" autocomplete, the value
 * is stored as `{{First_Name}}`, `{{Email}}`, etc.  Before sending these to
 * the TPA API they must be resolved to the actual contact/CRM property values.
 *
 * This mirrors the original app's `getTemplateAppliedMessage()` which is called
 * on every template placeholder value before the API call
 * (messagebird-rest-things.js line 664).
 *
 * @param vars      Template variables record (e.g. { "1": "{{First_Name}}", "2": "Hi" })
 * @param recipient The recipient whose properties supply the values
 * @returns A new record with all `{{property}}` tokens replaced by actual values
 */
export function resolveTemplateVariables(
  vars: Record<string, string>,
  recipient: {
    name?: string;
    phone?: string;
    email?: string;
    properties?: Record<string, string>;
  }
): Record<string, string> {
  const resolved: Record<string, string> = {};

  for (const [key, rawValue] of Object.entries(vars)) {
    let value = rawValue;

    // Resolve ${Module.FieldName} format (e.g. ${Contacts.First_Name})
    if (/\$\{[A-Za-z0-9_]+\.[A-Za-z0-9_]+\}/.test(value) && recipient.properties) {
      value = value.replace(/\$\{[A-Za-z0-9_]+\.([A-Za-z0-9_]+)\}/g, (_, field) => {
        return recipient.properties?.[field] ?? '';
      });
    }

    // Only process if the value contains {{...}} placeholders
    if (/\{\{\w+\}\}/.test(value)) {
      // Custom recipient/CRM properties first
      if (recipient.properties) {
        for (const [propKey, propVal] of Object.entries(recipient.properties)) {
          if (propVal) {
            value = value.replace(new RegExp(`\\{\\{${propKey}\\}\\}`, 'g'), propVal);
          }
        }
      }

      // Built-in field fallbacks
      if (recipient.name) {
        value = value.replace(/\{\{name\}\}/gi, recipient.name);
      }
      if (recipient.phone) {
        value = value.replace(/\{\{phone\}\}/gi, recipient.phone);
      }
      if (recipient.email) {
        value = value.replace(/\{\{email\}\}/gi, recipient.email);
      }

      // Strip any remaining unresolved placeholders (use dash like original app)
      value = value.replace(/\{\{\w+\}\}/g, '-');
    }

    resolved[key] = value;
  }

  return resolved;
}

/**
 * Personalise a message body for a specific recipient.
 *
 * Substitutes `{{name}}`, `{{phone}}`, `{{email}}` and any custom
 * recipient properties, then strips unmatched placeholders and
 * optionally appends a signature.
 */
export function personalizeMessage({
  body,
  recipient,
  signature,
  hasPlaceholders,
  hasProperties,
  isTemplate,
  currentUser,
}: PersonalizeOptions): string {
  let messageBody = body;

  // Resolve ${CURRENT_USER.field} placeholders (e.g. ${CURRENT_USER.Full_Name})
  if (currentUser) {
    const userFields: Record<string, string> = {
      Full_Name: currentUser.displayName || '',
      name: currentUser.displayName || '',
      email: currentUser.email || '',
      phone: currentUser.phoneNumber || '',
    };
    messageBody = messageBody.replace(/\$\{CURRENT_USER\.([A-Za-z0-9_]+)\}/g, (_, field) => {
      return userFields[field] || '';
    });
  }

  // Resolve ${Module.FieldName} placeholders (e.g. ${Contacts.First_Name})
  // The module prefix is stripped — field is looked up from recipient properties
  if (recipient.properties) {
    messageBody = messageBody.replace(/\$\{[A-Za-z0-9_]+\.([A-Za-z0-9_]+)\}/g, (_, field) => {
      return recipient.properties?.[field] ?? '';
    });
  }

  if (hasPlaceholders && hasProperties && !isTemplate) {
    // {{field}} format — custom recipient properties
    if (recipient.properties) {
      for (const [key, value] of Object.entries(recipient.properties)) {
        messageBody = messageBody.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value ?? '');
      }
    }

    // Built-in fields
    messageBody = messageBody.replace(/\{\{name\}\}/g, recipient.name);
    messageBody = messageBody.replace(/\{\{phone\}\}/g, recipient.phone);
    if (recipient.email) {
      messageBody = messageBody.replace(/\{\{email\}\}/g, recipient.email);
    }
  }

  // Strip any remaining unmatched placeholders — never send raw ${…} or {{…}} in messages
  messageBody = messageBody.replace(/\$\{[A-Za-z0-9._\-]+\}/g, '');
  messageBody = messageBody.replace(/\{\{[\w-]+\}\}/g, '');

  if (signature) messageBody += '\n' + signature;

  return messageBody;
}
